<template>
  <b-modal
    id="modal-compliance-unlock"
    :title="`Unlock compliance — ${studentName}`"
    :visible="visible"
    ok-title="Unlock"
    ok-variant="danger"
    :ok-disabled="!canSubmit"
    :cancel-disabled="isSubmitting"
    :no-close-on-backdrop="true"
    :no-close-on-esc="isSubmitting"
    centered
    size="md"
    @hidden="onHidden"
    @ok="onOk"
  >
    <p class="mb-1">
      A locked section is frozen deliberately — its score stops tracking the Learn Portal. Unlocking lets it resync, and
      the reason below is recorded against your account in the audit trail.
    </p>

    <b-alert v-if="lockedSections.length === 0" variant="warning" show class="mb-0 p-1">
      Nothing on this profile is locked.
    </b-alert>

    <template v-else>
      <b-form-group label="Release" label-for="compliance-unlock-scope">
        <b-form-select id="compliance-unlock-scope" v-model="scope" :options="scopeOptions" :disabled="isSubmitting" />
      </b-form-group>

      <b-form-group :label="`Locked ${lockedSections.length === 1 ? 'section' : 'sections'}`">
        <ul class="list-unstyled mb-0">
          <li
            v-for="section in lockedSections"
            :key="section.id"
            class="d-flex align-items-center justify-content-between py-25"
            :class="{ 'text-muted': scope !== null && scope !== section.id }"
          >
            <span class="d-flex align-items-center" style="gap: 0.4rem">
              <font-awesome-icon icon="fa-regular fa-lock" />
              <span>{{ section.name }}</span>
            </span>

            <span class="d-flex align-items-center" style="gap: 0.4rem">
              <b-badge variant="light-secondary" class="text-capitalize">{{ section.status }}</b-badge>
              <b-badge variant="light-primary">{{ section.score }}</b-badge>
            </span>
          </li>
        </ul>
      </b-form-group>

      <b-form-group label="Reason" label-for="compliance-unlock-reason">
        <b-form-textarea
          id="compliance-unlock-reason"
          v-model="reason"
          autofocus
          rows="2"
          max-rows="4"
          trim
          :disabled="isSubmitting"
          :state="reasonState"
          placeholder="e.g. locked in error — student is not TOC"
          @blur="touched = true"
        />
        <b-form-invalid-feedback :state="reasonState"> Give a reason of at least 3 characters. </b-form-invalid-feedback>
      </b-form-group>
    </template>
  </b-modal>
</template>

<script>
import {
  BAlert,
  BBadge,
  BFormGroup,
  BFormInvalidFeedback,
  BFormSelect,
  BFormTextarea,
  BModal,
} from 'bootstrap-vue';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { gradeScore, title } from '@core/utils/filter';

const MIN_REASON = 3;

export default {
  name: 'ComplianceUnlockModal',
  components: {
    BAlert,
    BBadge,
    BFormGroup,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTextarea,
    BModal,
  },
  props: {
    /** The roster profile row. Reads `compliance.sections` to find the locks. */
    profile: { type: Object, default: null },
    /** Cohort sections, used only to give each locked section a readable name. */
    sections: { type: Array, default: () => [] },
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      // null means "every locked section", matching the API, where omitting
      // sectionId releases the whole record.
      scope: null,
      reason: '',
      isSubmitting: false,
      touched: false,
    };
  },
  computed: {
    studentName() {
      const student = this.profile && this.profile.student;
      if (!student) return 'this student';
      return `${student.firstName} ${student.lastName}`.trim();
    },
    lockedSections() {
      const compliance = this.profile && this.profile.compliance;
      if (!compliance || !compliance.sections) return [];

      const names = new Map((this.sections || []).map((section) => [section.id, section.name]));

      return Object.keys(compliance.sections)
        .filter((id) => compliance.sections[id].locked)
        .map((id) => {
          const entry = compliance.sections[id];
          return {
            id,
            // Section names carry a " | ..." suffix in the roster views; drop it so the
            // list stays readable at modal width. Falls back to the id when the cohort
            // no longer lists the section (an orphaned compliance entry).
            name: (names.get(id) || id).replace(/ \| .*/, ''),
            status: title(entry.status || 'unknown'),
            score: gradeScore({ score: (entry.scores || {}).overall, precision: 2, percent: true }),
          };
        });
    },
    scopeOptions() {
      const count = this.lockedSections.length;
      return [
        { value: null, text: `All locked sections (${count})` },
        ...this.lockedSections.map((section) => ({ value: section.id, text: section.name })),
      ];
    },
    reasonState() {
      // Stays null until the field is touched, so the box is not red before the
      // operator has had a chance to type.
      if (!this.touched && this.reason.length === 0) return null;
      return this.reason.trim().length >= MIN_REASON;
    },
    canSubmit() {
      return !this.isSubmitting && this.lockedSections.length > 0 && this.reason.trim().length >= MIN_REASON;
    },
  },
  watch: {
    visible(isOpen) {
      if (!isOpen) return;
      this.scope = null;
      this.reason = '';
      this.touched = false;
      this.isSubmitting = false;
    },
  },
  methods: {
    onHidden() {
      if (this.isSubmitting) return;
      this.$emit('hidden');
    },
    onOk(event) {
      if (event) event.preventDefault();

      this.touched = true;
      if (!this.canSubmit) return;

      this.isSubmitting = true;

      store
        .dispatch('rosterStoreModule/unlockProfileCompliance', {
          profileId: this.profile.id,
          sectionId: this.scope || undefined,
          reason: this.reason.trim(),
        })
        .then((response) => {
          const released = (response && response.data && response.data.sectionIds) || [];

          this.$toast({
            component: ToastificationContent,
            props: {
              title: `Unlocked ${released.length || this.lockedSections.length} section${
                released.length === 1 ? '' : 's'
              }`,
              text: 'The released sections will resync from the Learn Portal.',
              icon: 'UnlockIcon',
              variant: 'success',
            },
          });

          this.$emit('unlocked');
          this.$emit('hidden');
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to unlock compliance',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.response ? error.response.data.message : error.message,
            },
          });
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
  },
};
</script>
